import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { TrendingUp } from 'lucide-react';
import { ScoreGauge } from '@/components/report/score-gauge';
import { useCountUp, useMotionAllowed } from '@/hooks/use-motion';
import { cn, scoreTone } from '@/lib/utils';

/** The story the card tells: a struggling site, then the same site after fixes. */
const PHASES = {
  before: {
    label: 'Before',
    score: 54,
    issues: '7 issues to fix',
    rows: [58, 41, 47, 72, 50],
    holdMs: 3800,
    countMs: 700,
  },
  after: {
    label: 'After fixes',
    score: 92,
    issues: '1 issue left',
    rows: [94, 90, 88, 97, 91],
    holdMs: 5200,
    countMs: 1600,
  },
} as const;
type Phase = keyof typeof PHASES;

const ROW_LABELS = ['Technical SEO', 'Local SEO', 'Content', 'Mobile', 'Restaurant experience'];
const GAIN = PHASES.after.score - PHASES.before.score;

function RowValue({ value, run }: { value: number; run: boolean }) {
  return <>{useCountUp(value, { start: run, durationMs: 1100 })}</>;
}

/**
 * Hero "example report" that loops Before → After fixes to show the product's promise.
 * Pauses when off-screen, hovered, or the tab is hidden; static for reduced-motion users.
 */
export function ExampleScoreCard() {
  const ref = useRef<HTMLElement | null>(null);
  const [onScreen, setOnScreen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [loopPhase, setPhase] = useState<Phase>('before');
  const [loopStarted, setStarted] = useState(false);
  // Server render, hydration and reduced-motion users get the finished "after" state (the
  // real story for crawlers/no-JS); in the browser the loop then starts from "before".
  const animated = useMotionAllowed();
  const phase: Phase = animated ? loopPhase : 'after';
  const started = animated ? loopStarted : true;

  // Track visibility continuously (not just once) so the loop pauses off-screen.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([e]) => {
        const visible = Boolean(e?.isIntersecting);
        setOnScreen(visible);
        if (visible) setStarted(true);
      },
      { threshold: 0.3 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onVis = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const running = animated && started && onScreen && !hovered && pageVisible;
  useEffect(() => {
    if (!running) return;
    const t = setTimeout(
      () => setPhase((p) => (p === 'before' ? 'after' : 'before')),
      PHASES[phase].holdMs,
    );
    return () => clearTimeout(t);
  }, [running, phase]);

  const current = PHASES[phase];
  const isAfter = phase === 'after';

  return (
    <div className="animate-fade-up delay-var" style={{ '--delay': '450ms' } as CSSProperties}>
      <figure
        ref={ref}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="animate-float rounded-3xl bg-white p-6 shadow-xl shadow-brand-900/10 ring-1 ring-ink-200 transition-shadow duration-300 hover:shadow-2xl"
        aria-label={`Example report for Joe’s Italian Kitchen: the score improves from ${PHASES.before.score} before fixes to ${PHASES.after.score} after fixes.`}
      >
        <figcaption className="flex items-center justify-between gap-3" aria-hidden>
          <span className="text-xs font-semibold uppercase tracking-widest text-ink-500">
            Example report
          </span>
          <span
            key={phase}
            className={cn(
              'animate-scale-in rounded-full px-2.5 py-0.5 text-xs font-bold ring-1',
              isAfter
                ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                : 'bg-amber-50 text-amber-800 ring-amber-200',
            )}
          >
            {current.label}
          </span>
        </figcaption>

        <div className="mt-4 flex items-center gap-6" aria-hidden>
          <ScoreGauge
            score={current.score}
            size={140}
            label="Example score"
            durationMs={current.countMs}
          />
          <div className="min-w-0">
            <p className="font-display text-xl font-bold">Joe’s Italian Kitchen</p>
            <p className="text-sm text-ink-500">Austin, TX</p>
            <p
              key={`issues-${phase}`}
              className="animate-fade-in mt-2 text-sm font-medium text-ink-600"
            >
              {current.issues}
            </p>
            {isAfter && (
              <span
                className="animate-pop delay-var mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-bold text-white shadow-md shadow-emerald-900/20"
                style={{ '--delay': `${current.countMs - 200}ms` } as CSSProperties}
              >
                <TrendingUp className="size-3.5" /> +{GAIN} points
              </span>
            )}
          </div>
        </div>

        <ul className="mt-6 space-y-2.5" aria-hidden>
          {ROW_LABELS.map((label, i) => {
            const v = current.rows[i]!;
            return (
              <li key={label} className="text-sm">
                <div className="flex justify-between">
                  <span className="text-ink-600">{label}</span>
                  <span className="font-semibold tabular-nums">
                    <RowValue value={v} run={started} />
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink-100">
                  <div
                    className={cn(
                      'h-full w-full origin-left rounded-full transition-[transform,background-color] duration-1000 ease-out',
                      scoreTone(v).bg,
                    )}
                    style={{
                      transform: `scaleX(${started ? v / 100 : 0})`,
                      transitionDelay: `${i * 90}ms`,
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </figure>
    </div>
  );
}
